import { defineStore } from 'pinia'
import { fetchGlobalConfig } from '@ms/api/app'
import { divide10k, apiMoney } from '@ms/utils-es/math'
import { getConfigValue, getPercent, calcWinningTax, calcTaxBonus } from '@ms/components/tax/config.js'
import axios from 'crypto-fetch/axios'
import { formatNumber } from '@ms/utils/format'

export const useGlobalConfigStore = defineStore('globalConfig', {
  state: () => ({
    loadingConfig: false, // true, false, -1
    // 全局配置，包含 realsport/jackpot/virtual, deposit/withdraw, cashout 等相关配置。请从 getters 取值
    config: {},
    loadedConfig: false,
    // quickStake查询存储
    quickStake: [],
    // quickStake相关操作中数据存储
    stakeSetting: [],
    stakeSubmit: []
  }),
  getters: {
    configDeposit() {
      const { depositMin, depositMax, depositMaxTenn, depositMaxOpayCashier } = this.config
      const depositMinAmount = divide10k(depositMin)
      return {
        depositMinAmount,
        depositMaxAmount: divide10k(depositMax),
        depositMaxTenn: divide10k(depositMaxTenn),
        depositMaxOpayCashier: divide10k(depositMaxOpayCashier),
        placeholderOfAmount: depositMinAmount ? `min.${depositMinAmount}.00` : ''
      }
    },
    configWithdraw() {
      const { withdrawMin, withdrawMax, withdrawMaxTenn, withdrawOnceFee, withdrawFreeMinAmount } = this.config
      const withdrawMinAmount = divide10k(withdrawMin)
      const placeholderOfAmount = withdrawMinAmount ? `min.${withdrawMinAmount}.00` : ''
      const withdrawFee = divide10k(withdrawOnceFee)
      const withdrawFeeThreshold = divide10k(withdrawFreeMinAmount)
      const parsedFeeThreshold = withdrawFeeThreshold > 0 ? formatNumber(withdrawFeeThreshold, 2) : 0

      return {
        withdrawMinAmount,
        withdrawMaxAmount: divide10k(withdrawMax),
        withdrawMaxTenn: divide10k(withdrawMaxTenn),
        withdrawFee,
        withdrawFeeThreshold,
        placeholderOfAmount,
        // 格式化提现手续费，用来展示
        parsedWithdrawFee: formatNumber(withdrawFee, 2),
        parsedFeeThreshold
      }
    },
    configJackpot() {
      const { jpMinBet, jpTaxBL, jpTaxL } = this.config
      // Jackpot 税补偿比例A
      const jackpotTaxBL = parseFloat(jpTaxBL || 0)
      // Jackpot 税比例A
      const jackpotTaxL = parseFloat(jpTaxL || 0)
      return {
        jackpotMinStake: divide10k(jpMinBet),
        hasTaxBonus: jackpotTaxBL > 0,
        hasTax: jackpotTaxL > 0,
        jackpotTaxPercent: jackpotTaxL * 100
      }
    },
    // live 列表定时刷新间隔）对应公共配置，单位 ms
    refreshLiveMillisecond: (state) => (state.config.refreshLiveSeconds || 60) * 1000,
    // 需要取消订阅的 marketId 列表
    igopmsMarkets: (state) => state.config.igopms?.split(',').filter((x) => !!x) || [],
    // betslip 默认投注金额
    betSlipDefaultStake: (state) => state.quickStake[0] || state.minStake,
    // realsport最小投注额
    minStake: (state) => divide10k(state.config.rsgMinBet),
    minStakePlaceholder: (state) => `min. ${state.minStake}`,
    // realsport最大投注额
    maxStake: (state) => divide10k(state.config.rsgMaxBet),
    // 最大可能中奖金额
    potWinMax: (state) => divide10k(state.config.potWinMax),
    // 最大 odds 限制
    maxLimitOdds: (state) => divide10k(state.config.potOddsMax),
    // BetBuilder 是否支持混投 true为支持，false为不支持
    isBetBuilderCanMixBet() {
      return !!this.config.isBetBuilderCanMixBet
    },

    // 方案一最小 odds
    flexibleMinOdds: (state) => divide10k(state.config.flexibleMinOdds),
    // 方案二最小 odds
    suppleMinOdds: (state) => divide10k(state.config.suppleMinOdds),
    // 是否阻止live的multiple+
    notSupportLiveMagic: (state) => state.config.banLiveMagicBet === 'true',
    // 后台配置方案一是否关闭
    notSupportFlexible: (state) => state.config.banFlexible === 'true',
    // 后台配置方案二是否关闭
    notSupportSupple: (state) => state.config.banSupple === 'true',
    // magicNumber 最小数量
    minMagicSelectionNumber: (state) => parseFloat(state.config.minMagicSelectionNumber || 0),
    // 对于特性选项个数，最小中奖个数限制，通过这个配置防止计算量过大, 如 { 30:26 }, //30个选项串，最小multiple+的正确选项数量为26，也就是下单参数的magicNumber>=26
    magicCombsMap: (state) => JSON.parse(state.config.constrainedMagicNumber || '{}'),
    flexibleBonusMinOdds: (state) => divide10k(state.config.flexibleBonusMinOdds),
    flexibleBonusRatio: (state) => JSON.parse(state.config.flexibleBonusRatio || '{}'),
    flexibleSafetyRatio: (state) => parseFloat(state.config.flexibleSafetyRatio || 0),
    suppleMaxOddsSafetyRatio: (state) => parseFloat(state.config.suppleMaxOddsSafetyRatio || 0),
    suppleMinOddsSafetyRatio: (state) => parseFloat(state.config.suppleMinOddsSafetyRatio || 0),

    // tax
    // 真实体育税比例A
    taxOfWinning: ({ config }) => getConfigValue(config.rsTaxL),
    // 真实体育税系数B
    taxOfWinningB: ({ config }) => getConfigValue(config.rsTaxR),
    percentTaxOfWinning: (state) => getPercent(state.taxOfWinning),
    // 真实体育税补偿比例A
    taxOfExtraBonus: ({ config }) => getConfigValue(config.rsTaxBL),
    // 真实体育税补偿比例B
    taxOfExtraBonusB: ({ config }) => getConfigValue(config.rsTaxBR),
    percentTaxOfExtraBonus: (state) => getPercent(state.taxOfExtraBonus),

    // 虚拟体育税比例A
    vsTaxOfWinning: ({ config }) => getConfigValue(config.vsTaxL),
    // 虚拟体育税系数B
    vsTaxOfWinningB: ({ config }) => getConfigValue(config.vsTaxR),
    vsPercentTaxOfWinning: (state) => getPercent(state.vsTaxOfWinning),
    // 虚拟体育税补偿比例A
    vsTaxOfExtraBonus: ({ config }) => getConfigValue(config.vsTaxBL),
    // 虚拟体育税补偿比例B
    vsTaxOfExtraBonusB: ({ config }) => getConfigValue(config.vsTaxBR),
    vsPercentTaxOfExtraBonus: (state) => getPercent(state.vsTaxOfExtraBonus),

    // 高频游戏税补偿比例A
    hfTaxBL: (state) => getConfigValue(state.config.hfTaxBL),
    // 高频游戏税补偿比例B
    hfTaxBR: (state) => getConfigValue(state.config.hfTaxBR),
    // 高频游戏税比例A
    hfTaxL: (state) => getConfigValue(state.config.hfTaxL),
    // 高频游戏税系数B
    hfTaxR: (state) => getConfigValue(state.config.hfTaxR),

    // 是否支持V-Soccer Multiple+ 功能,1支持
    vSoccerMagicBetting: (state) => state.config.vSoccerMagicBetting === 1,
    // 是否支持V-Soccer与其他运动混合投注,1支持
    vSoccerMixedBetting: (state) => state.config.vSoccerMixedBetting === 1,
    transactionListTaxFilterEnabled() {
      return !!this.config.transactionListTaxFilterEnabled
    },
    transactionListBonusFilterEnabled() {
      return !!this.config.transactionListBonusFilterEnabled
    },
    // Footer 里的联系电话
    telContact() {
      const { customerServicePhoneNumber: num } = this.config
      if (num === 'undefined' || !num) return null
      try {
        const arr = JSON.parse(num)
        if (arr.length === 0) return null
        return arr.map((num) => ({
          label: num,
          value: num.replace(/\D/g, '')
        }))
      } catch (error) {
        console.error('Error customerServicePhoneNumber:', num, error)
        return null
      }
    }
  },
  actions: {
    updateLoading(bool = false) {
      this.loadingConfig = bool
    },
    /**
     * 查询全局配置
     * @return {Object} 返回全局配置对象
     */
    async getGlobalConfig() {
      try {
        this.updateLoading(true)
        const data = await fetchGlobalConfig()
        if (data) {
          if (!this.config.minStake) {
            this.config = data
          }
          this.loadedConfig = true
          this.updateLoading(false)
        } else {
          this.updateLoading(-1)
        }
      } catch (error) {
        this.updateLoading(-1)
        console.error(error)
      }
    },
    /**
     * calculate tax info
     * @param {Number} winningAmount winning amount initial
     * @param {Number} stake
     * @param {Number} stakeRatio  pay with voucher ratio
     * @param {Number} bizType  1 - realsport, 2 - virtual
     * @returns {Object} { winningTax, extraBonus }
     */
    calcTaxInfo(winningAmount, stake, stakeRatio = 1, bizType = 1) {
      const isRealsport = bizType === 1
      const winningTax = calcWinningTax({
        winningAmount,
        stake,
        taxA: isRealsport ? this.taxOfWinning : this.vsTaxOfWinning,
        taxB: isRealsport ? this.taxOfWinningB : this.vsTaxOfWinningB
      })

      const extraBonus = calcTaxBonus({
        winningAmount,
        stake,
        taxA: isRealsport ? this.taxOfExtraBonus : this.vsTaxOfExtraBonus,
        taxB: isRealsport ? this.taxOfExtraBonusB : this.vsTaxOfExtraBonusB,
        stakeRatio
      })

      return {
        winningTax,
        extraBonus
      }
    },
    // 快速投注编辑页信息保存
    stakeSettingInfo(data) {
      this.stakeSetting = data
    },
    submitStakes(data) {
      this.stakeSubmit = data
    },

    // 更新获取
    updateQuickStake(data) {
      this.quickStake = data.map((x) => x / 10000)
    },

    // 获取快速投注选项数据
    async fetchQuickSkate() {
      const data = await axios.get('/common/config/custom/stake')
      this.updateQuickStake(data)
    },

    // 提交快速投注选项编辑数据
    pushQuickStake() {
      const curStakes = this.stakeSubmit.map((x) => apiMoney(x))
      const body = curStakes.filter((x) => x !== 0)
      return axios.post('/common/config/custom/stake', body)
    }
  }
})
